import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';

const GEMINI_REST_BASE = 'https://api.gemini.com';
const GEMINI_WS_URL = 'wss://ws.gemini.com';
const GEMINI_BOOK_DEPTH = 20;
const GEMINI_MAX_CANDLES = 1440;
const GEMINI_MAX_TRADES = 100;

export const geminiRestClient = proxyRestOptions.client!;

type GeminiNumber = number | string;
type GeminiBookTuple = [price: GeminiNumber, amount: GeminiNumber];

interface GeminiMarketDetails {
  symbol: string;
  base_currency: string;
  quote_currency: string;
  tick_size: GeminiNumber;
  quote_increment: GeminiNumber;
  min_order_size: GeminiNumber;
  status: string;
  product_type: string;
}

interface GeminiV2Ticker {
  symbol: string;
  open: GeminiNumber;
  high: GeminiNumber;
  low: GeminiNumber;
  close: GeminiNumber;
  bid: GeminiNumber;
  ask: GeminiNumber;
  changes: GeminiNumber[];
}

interface GeminiLegacyTicker {
  bid: GeminiNumber;
  ask: GeminiNumber;
  last: GeminiNumber;
  volume: Record<string, GeminiNumber>;
}

interface GeminiTicker extends GeminiV2Ticker {
  volume: Record<string, GeminiNumber>;
}

interface GeminiBookLevel {
  price: GeminiNumber;
  amount: GeminiNumber;
  timestamp?: GeminiNumber;
}

export interface GeminiOrderbookSnapshot {
  bids: Array<GeminiBookLevel | GeminiBookTuple>;
  asks: Array<GeminiBookLevel | GeminiBookTuple>;
}

export type GeminiCandle = [
  timestamp: GeminiNumber,
  open: GeminiNumber,
  high: GeminiNumber,
  low: GeminiNumber,
  close: GeminiNumber,
  volume: GeminiNumber,
];

interface GeminiTrade {
  tid: GeminiNumber;
  timestamp: GeminiNumber;
  timestampms: GeminiNumber;
  price: GeminiNumber;
  amount: GeminiNumber;
  exchange: string;
  type: string;
}

interface GeminiWsError {
  code?: number;
  msg?: string;
}

interface GeminiWsMessage {
  id?: string;
  status?: number;
  error?: GeminiWsError;
  e?: string;
  E?: GeminiNumber;
  s?: string;
  u?: GeminiNumber;
  lastUpdateId?: GeminiNumber;
  b?: GeminiNumber;
  B?: GeminiNumber;
  a?: GeminiNumber;
  A?: GeminiNumber;
  c?: GeminiNumber;
  C?: GeminiNumber;
  bids?: GeminiBookTuple[];
  asks?: GeminiBookTuple[];
  t?: GeminiNumber;
  p?: GeminiNumber;
  q?: GeminiNumber;
  m?: boolean;
}

export interface GeminiOrderbookState {
  bids: Map<string, string>;
  asks: Map<string, string>;
}

const GEMINI_TIMEFRAMES: Partial<Record<IChartTimeframe, string>> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '6h': '6h',
  '24h': '1d',
  '1d': '1d',
};

const isFiniteNumber = (value: unknown) =>
  value !== null &&
  value !== undefined &&
  (typeof value !== 'string' || value.trim() !== '') &&
  Number.isFinite(Number(value));

const asString = (value: GeminiNumber) => String(value);

const normalizeBookLevels = (
  levels: Array<GeminiBookLevel | GeminiBookTuple> | undefined,
): string[][] =>
  (levels ?? [])
    .map(level => {
      if (Array.isArray(level)) return [level[0], level[1]];
      return [level?.price, level?.amount];
    })
    .filter(
      (level): level is [GeminiNumber, GeminiNumber] =>
        isFiniteNumber(level[0]) && isFiniteNumber(level[1]) && Number(level[1]) > 0,
    )
    .map(([price, amount]) => [asString(price), asString(amount)]);

const calculateChange = (last: GeminiNumber, open: GeminiNumber): string => {
  const lastNumber = Number(last);
  const openNumber = Number(open);
  if (!Number.isFinite(lastNumber) || !Number.isFinite(openNumber) || openNumber === 0) return '0';
  return normalizeNumber(((lastNumber - openNumber) / openNumber) * 100);
};

export const buildGeminiMarketSymbol = (base: string, quote: string) =>
  `${base.toLowerCase()}${quote.toLowerCase()}`;

export const geminiPrecisionFromTick = (tick: GeminiNumber | undefined): number => {
  if (!isFiniteNumber(tick) || Number(tick) <= 0) return 1;
  const normalized = String(tick).toLowerCase();
  if (normalized.includes('e-')) {
    const exponent = Number(normalized.split('e-')[1]);
    return Number.isFinite(exponent) ? exponent : 1;
  }
  const [, decimals = ''] = normalized.split('.');
  return decimals.replace(/0+$/, '').length;
};

export const normalizeGeminiCandles = (candles: GeminiCandle[]): KLine[] =>
  candles
    .filter(
      candle =>
        Array.isArray(candle) && candle.length >= 6 && candle.slice(0, 6).every(isFiniteNumber),
    )
    .map(
      ([timestamp, open, high, low, close, volume]) =>
        [
          Number(timestamp),
          asString(open),
          asString(high),
          asString(low),
          asString(close),
          asString(volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const createGeminiOrderbookState = (): GeminiOrderbookState => ({
  bids: new Map(),
  asks: new Map(),
});

export const applyGeminiOrderbookSnapshot = (
  state: GeminiOrderbookState,
  snapshot: GeminiOrderbookSnapshot,
) => {
  const bids = normalizeBookLevels(snapshot.bids);
  const asks = normalizeBookLevels(snapshot.asks);
  if (bids.length === 0 || asks.length === 0) {
    throw new Error('Gemini public orderbook snapshot is empty');
  }

  state.bids.clear();
  state.asks.clear();
  bids.forEach(([price, amount]) => state.bids.set(price, amount));
  asks.forEach(([price, amount]) => state.asks.set(price, amount));
};

export const buildGeminiSubscription = (symbol: string, id: string) => ({
  id,
  method: 'subscribe' as const,
  params: [`${symbol}@bookTicker`, `${symbol}@depth20@100ms`, `${symbol}@trade`],
});

export class GeminiStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  base: string;
  quote: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  orderbook = createGeminiOrderbookState();
  lastTicker: ITickerSocket | undefined;
  liveCandle: KLine | undefined;
  liveIntervalMs = 60_000;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.base = base.toUpperCase();
    this.quote = quote.toUpperCase();
    this.pair = buildGeminiMarketSymbol(base, quote);
    this.displayDepth = Math.min(GEMINI_BOOK_DEPTH, orderbookDepth ?? GEMINI_BOOK_DEPTH);
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    const bids = [...this.orderbook.bids.entries()]
      .sort((left, right) => Number(right[0]) - Number(left[0]))
      .slice(0, this.displayDepth);
    const asks = [...this.orderbook.asks.entries()]
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .slice(0, this.displayDepth);
    this.onOrderbookUpdate({ bids, asks });
  };

  publishTicker = (ticker: GeminiTicker) => {
    if (
      ticker.symbol.toLowerCase() !== this.pair ||
      !isFiniteNumber(ticker.open) ||
      !isFiniteNumber(ticker.high) ||
      !isFiniteNumber(ticker.low) ||
      !isFiniteNumber(ticker.close)
    ) {
      throw new Error(`Gemini ticker ${this.pair} has an invalid shape`);
    }
    const baseVolume = ticker.volume[this.base];
    const quoteVolume = ticker.volume[this.quote];
    if (!isFiniteNumber(baseVolume) || !isFiniteNumber(quoteVolume)) {
      throw new Error(`Gemini ticker ${this.pair} has no matching public volumes`);
    }

    const last = asString(ticker.close);
    this.lastTicker = {
      baseVolume: asString(baseVolume),
      change: calculateChange(ticker.close, ticker.open),
      close: last,
      high: asString(ticker.high),
      last,
      low: asString(ticker.low),
      quoteVolume: asString(quoteVolume),
    };
    this.onTickerUpdate(this.lastTicker);
  };

  publishLivePrice = (priceValue: GeminiNumber) => {
    if (!isFiniteNumber(priceValue)) return;
    const price = asString(priceValue);
    this.lastTicker = {
      baseVolume: this.lastTicker?.baseVolume ?? '',
      change: this.lastTicker?.change ?? '',
      close: price,
      high: this.lastTicker?.high ?? '',
      last: price,
      low: this.lastTicker?.low ?? '',
      quoteVolume: this.lastTicker?.quoteVolume ?? '',
    };
    this.onTickerUpdate(this.lastTicker);
  };

  publishTrade = (message: GeminiWsMessage) => {
    if (
      message.s?.toLowerCase() !== this.pair ||
      !isFiniteNumber(message.t) ||
      !isFiniteNumber(message.p) ||
      !isFiniteNumber(message.q) ||
      !isFiniteNumber(message.E)
    ) {
      throw new Error(`Gemini public trade ${String(message.t ?? 'unknown')} is invalid`);
    }

    const timestamp = Math.floor(Number(message.E) / 1_000_000);
    if (!Number.isSafeInteger(timestamp) || timestamp <= 0) {
      throw new Error(`Gemini public trade timestamp ${String(message.E)} is invalid`);
    }
    const price = asString(message.p!);
    const quantity = asString(message.q!);
    const side = message.m ? 'sell' : 'buy';
    this.onTradeUpdate({ price, quantity, timestamp: String(timestamp), side });
    this.publishLivePrice(price);

    const bucket = Math.floor(timestamp / this.liveIntervalMs) * this.liveIntervalMs;
    if (!this.liveCandle || this.liveCandle[0] !== bucket) {
      this.liveCandle = [bucket, price, price, price, price, quantity];
    } else {
      this.liveCandle = [
        bucket,
        this.liveCandle[1],
        normalizeNumber(Math.max(Number(this.liveCandle[2]), Number(price))),
        normalizeNumber(Math.min(Number(this.liveCandle[3]), Number(price))),
        price,
        normalizeNumber(Number(this.liveCandle[5]) + Number(quantity)),
      ];
    }
    this.klineUpdate(this.liveCandle);
  };

  fetchMarket = async () => {
    try {
      const response = await geminiRestClient.get<GeminiMarketDetails>(
        `${GEMINI_REST_BASE}/v1/symbols/details/${this.pair}`,
      );
      const market = response.data;
      if (
        market.symbol.toLowerCase() !== this.pair ||
        market.base_currency.toUpperCase() !== this.base ||
        market.quote_currency.toUpperCase() !== this.quote ||
        market.status.toLowerCase() !== 'open' ||
        market.product_type.toLowerCase() !== 'spot' ||
        !isFiniteNumber(market.quote_increment) ||
        Number(market.quote_increment) <= 0
      ) {
        throw new Error(`Gemini market ${this.pair} is unavailable`);
      }
      this.priceTick = asString(market.quote_increment);
      this.precision = geminiPrecisionFromTick(market.quote_increment);
      this.selectedPrecision = this.precision;
      this.precisionUpdate(this.precision);
    } catch (error) {
      console.log('Gemini market error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await geminiRestClient.get<GeminiOrderbookSnapshot>(
        `${GEMINI_REST_BASE}/v1/book/${this.pair}`,
        {
          params: {
            limit_asks: GEMINI_BOOK_DEPTH,
            limit_bids: GEMINI_BOOK_DEPTH,
          },
        },
      );
      applyGeminiOrderbookSnapshot(this.orderbook, response.data);
      this.publishOrderbook();
    } catch (error) {
      console.log('Gemini orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const [v2Response, legacyResponse] = await Promise.all([
        geminiRestClient.get<GeminiV2Ticker>(`${GEMINI_REST_BASE}/v2/ticker/${this.pair}`),
        geminiRestClient.get<GeminiLegacyTicker>(`${GEMINI_REST_BASE}/v1/pubticker/${this.pair}`),
      ]);
      const close = isFiniteNumber(legacyResponse.data.last)
        ? legacyResponse.data.last
        : v2Response.data.close;
      this.publishTicker({
        ...v2Response.data,
        close,
        volume: legacyResponse.data.volume,
      });
    } catch (error) {
      console.log('Gemini ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const geminiInterval = GEMINI_TIMEFRAMES[interval];
    if (!geminiInterval) return [];
    const boundedLimit = Math.min(GEMINI_MAX_CANDLES, Math.max(1, limit));

    try {
      const response = await geminiRestClient.get<GeminiCandle[]>(
        `${GEMINI_REST_BASE}/v2/candles/${this.pair}/${geminiInterval}`,
      );
      return normalizeGeminiCandles(response.data ?? [])
        .filter(candle => (from <= 0 || candle[0] >= from) && (to <= 0 || candle[0] < to))
        .slice(-boundedLimit);
    } catch (error) {
      console.log('Gemini klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    const boundedLimit = Math.min(GEMINI_MAX_TRADES, Math.max(1, limit));
    try {
      const response = await geminiRestClient.get<GeminiTrade[]>(
        `${GEMINI_REST_BASE}/v1/trades/${this.pair}`,
        { params: { limit_trades: boundedLimit } },
      );
      return (response.data ?? [])
        .filter(
          trade =>
            trade.exchange?.toLowerCase() === 'gemini' &&
            isFiniteNumber(trade.timestampms) &&
            isFiniteNumber(trade.price) &&
            isFiniteNumber(trade.amount) &&
            ['buy', 'sell'].includes(trade.type?.toLowerCase()),
        )
        .map(trade => ({
          price: asString(trade.price),
          quantity: asString(trade.amount),
          timestamp: asString(trade.timestampms),
          side: trade.type.toLowerCase(),
        }));
    } catch (error) {
      console.log('Gemini trades error:', error);
      return [];
    }
  };

  handleWebSocketMessage = (message: GeminiWsMessage) => {
    if (message.id !== undefined) {
      if (message.status !== 200 || message.error) {
        throw new Error(
          `Gemini WebSocket request ${message.id} failed with status ${String(message.status)}: ${String(message.error?.code ?? '')} ${message.error?.msg ?? ''}`,
        );
      }
      return;
    }
    if (message.error) {
      throw new Error(`Gemini WebSocket error ${String(message.error.code)}: ${message.error.msg}`);
    }

    if (Array.isArray(message.bids) && Array.isArray(message.asks)) {
      if (!isFiniteNumber(message.lastUpdateId)) {
        throw new Error('Gemini WebSocket orderbook update ID is invalid');
      }
      applyGeminiOrderbookSnapshot(this.orderbook, {
        bids: message.bids,
        asks: message.asks,
      });
      this.publishOrderbook();
      return;
    }

    if (
      message.s?.toLowerCase() === this.pair &&
      isFiniteNumber(message.u) &&
      isFiniteNumber(message.b) &&
      isFiniteNumber(message.B) &&
      isFiniteNumber(message.a) &&
      isFiniteNumber(message.A)
    ) {
      if (isFiniteNumber(message.c)) this.publishLivePrice(message.c!);
      return;
    }

    if (message.s?.toLowerCase() === this.pair && message.t !== undefined) {
      this.publishTrade(message);
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.liveIntervalMs = interval > 0 ? interval : 60_000;
    this.liveCandle = undefined;

    const ws = new ReconnectingWebSocket(GEMINI_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    this.ws = ws;
    ws.onopen = () => {
      this.orderbook.bids.clear();
      this.orderbook.asks.clear();
      ws.send(JSON.stringify(buildGeminiSubscription(this.pair, String(Date.now()))));
    };
    ws.onmessage = event => {
      try {
        if (typeof event.data !== 'string') return;
        this.handleWebSocketMessage(JSON.parse(event.data) as GeminiWsMessage);
      } catch (error) {
        console.log('Gemini websocket error:', error);
        void this.fetchOrderbook();
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = Math.min(GEMINI_BOOK_DEPTH, Math.max(1, depth));
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchMarket(), this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new GeminiStrategy(args);
    await bootstrapStrategy(strategy);
    return strategy;
  };
}
